import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Section, SkeletonHeadingItem, SkeletonItem } from '@atlaskit/menu';
import { DISMISS_STARTED, type ACTEvent } from '@atlassian/ad-control-toolkit';
import { AppRecommendationsProvider } from '@atlassian/app-recommendations-kit';

import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import Placeholder from '@atlassian/jira-placeholder';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useOnACTEvents } from '../../common/act-event-handler-hooks';
import { useExposureEvent } from '../../common/analytics-hooks';
import {
	EVENT_SOURCE_RECOMMENDATION,
	TEAM_NAME,
	PACKAGE_NAME,
	type CohortType,
} from '../../common/constants';
import { useAppRecommendations } from '../../common/recommendation-hooks';
import { RecommendationSection } from './recommendation-section';

export type RecommendationType = {
	isEligible: boolean;
	sessionId: string;
	cohort: CohortType | string;
};

const Recommendation = ({ isEligible, sessionId, cohort }: RecommendationType) => {
	const isExposureEventFired = useRef<boolean>(false);

	const [isDismissed, setIsDismissed] = useState(false);
	const { onACTEvents } = useOnACTEvents({
		cohort,
		sessionId,
	});

	const { entityId, contextResolver, isRecommendationLoaded } = useAppRecommendations({
		cohort,
		sessionId,
	});

	const { fireExposureEvent } = useExposureEvent({ cohort, sessionId });

	useEffect(() => {
		if (!isExposureEventFired.current && entityId) {
			fireExposureEvent({ entityId });
			isExposureEventFired.current = true;
		}
	}, [isExposureEventFired, entityId, fireExposureEvent]);

	const handleEvents = useCallback(
		(event: ACTEvent) => {
			if (entityId) {
				onACTEvents({ event, entityId });
			}

			if (event.type === DISMISS_STARTED) {
				setIsDismissed(true);
			}
		},
		[onACTEvents, entityId],
	);

	const showRecommendation = !isDismissed;

	if (!isEligible) {
		return null;
	}

	return (
		<Placeholder
			fallback={
				<Section hasSeparator>
					<SkeletonHeadingItem />
					<SkeletonItem hasIcon />
				</Section>
			}
			name="jira-apps-1p-recos-section"
		>
			<ContextualAnalyticsData sourceName={EVENT_SOURCE_RECOMMENDATION} sourceType={SCREEN}>
				<AppRecommendationsProvider contextResolver={contextResolver} onEvents={handleEvents}>
					{showRecommendation ? (
						<RecommendationSection
							entityId={entityId}
							sessionId={sessionId}
							isRecommendationLoaded={isRecommendationLoaded}
							cohort={cohort}
						/>
					) : (
						<div data-testid="apps-1p-recs.ui.recommendation.no-banner-fallback" />
					)}
				</AppRecommendationsProvider>
			</ContextualAnalyticsData>
		</Placeholder>
	);
};

const RecommendationWithBoundary = ({ isEligible, sessionId, cohort }: RecommendationType) => (
	<ErrorBoundary
		onError={(_, error) => {
			fireErrorAnalytics({
				meta: {
					id: `errorBoundary.${EVENT_SOURCE_RECOMMENDATION}`,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
			});
		}}
	>
		<Recommendation isEligible={isEligible} sessionId={sessionId} cohort={cohort} />
	</ErrorBoundary>
);

export { RecommendationWithBoundary as Recommendation };
